import { gql } from '@apollo/client'
import { DataProvider } from 'react-admin'

import { currentRole } from '@/authProvider'
import { convertApolloErrors, throwNotSupported } from '@/dataProvider'
import { flattenEdge, uncachedClient } from '@/stackGqlClient'

import { DTOSale } from '../functions/DTOSale'
import { Sale } from '../types/Sale'
import { SaleAggregations } from '../types/SaleAggregations'
import { getSales } from '../repository/getSales'

const DEFAULT_PAGINATION = { page: 1, perPage: 10 }

const sortFields = {
  name: 'NAME',
  activeAt: 'ACTIVE_AT',
  expiresAt: 'EXPIRES_AT',
  lineItemsCount: 'LINE_ITEMS_COUNT',
  refundedLineItemsCount: 'REFUNDED_LINE_ITEMS_COUNT',
  netLineItemsCount: 'NET_LINE_ITEMS_COUNT',
  priceInCents: 'PRICE_IN_CENTS',
  retailPriceInCents: 'RETAIL_PRICE_IN_CENTS'
}

const salesProvider: DataProvider = {
  // TODO: support signal param
  getList: async (_resource, { pagination, filter, sort }) => {
    const { page, perPage } = pagination || DEFAULT_PAGINATION
    const { publisherId, status } = filter
    const partnerRole = currentRole()

    return uncachedClient
      .query({
        query: gql`
          query (
            $organizationId: Int!
            $partnerType: PartnerType!
            $partnerId: Int!
            $publisherId: Int!
            $page: Int
            $perPage: Int
            $sort: PartnerSaleSort!
            $sortDirection: SortDirection!
            $searchTerm: String
            $saleStatus: PartnerSaleStatus!
          ) {
            viewer {
              ... on OrgUser {
                organization(id: $organizationId) {
                  sales(
                    partnerType: $partnerType
                    partnerId: $partnerId
                    page: $page
                    perPage: $perPage
                    publisherId: $publisherId
                    sort: $sort
                    sortDirection: $sortDirection
                    searchTerm: $searchTerm
                    saleStatus: $saleStatus
                  ) {
                    pageInfo {
                      totalPages
                      totalCount
                    }
                    edges {
                      lineItemsCount
                      refundedLineItemsCount
                      numSold
                      node {
                        id
                        databaseId
                        name
                        priceInCents
                        retailPriceInCents
                        maxAvailable
                        activeAt
                        expiresAt
                        slug
                        products(first: 10) {
                          edges {
                            node {
                              databaseId
                              name
                              vendorSku
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          perPage,
          page,
          saleStatus: status.toUpperCase(),
          publisherId: publisherId || 1,
          organizationId: partnerRole?.organizationId,
          partnerType: partnerRole?.type,
          partnerId: partnerRole?.id,
          sort:
            sortFields[sort?.field as keyof typeof sortFields] ||
            sortFields.lineItemsCount,
          sortDirection: sort?.order.toUpperCase(),
          searchTerm: filter?.name
        }
      })
      .then((result) => ({
        data: result.data.viewer.organization.sales.edges.map(
          (e: SaleAggregations) => {
            return formatSaleEdge(e)
          }
        ),
        total: result.data.viewer.organization.sales.pageInfo.totalCount
      }))
      .catch(convertApolloErrors)
  },
  getOne: async (_resource, { id }) => {
    try {
      const parsedSaleData = await getSales(id as string)

      return {
        data: parsedSaleData
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any
    } catch (error) {
      console.error({ error })
    }
  },
  getMany: throwNotSupported,
  getManyReference: throwNotSupported,
  update: throwNotSupported,
  updateMany: throwNotSupported,
  create: throwNotSupported,
  delete: throwNotSupported,
  deleteMany: throwNotSupported
}

function formatSaleEdge(edge: SaleAggregations) {
  const { databaseId, ...rest } = flattenEdge(edge)

  const sale: Sale = Object.assign(rest, { id: databaseId })

  const formattedSale = DTOSale(sale)

  return formattedSale
}
export default salesProvider
